import { useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  decrementZoomScale,
  incrementZoomScale,
  resetBaseState,
  rotateDocument,
  setZoomScale,
  setZoomVisualValue,
} from 'dashboard/redux/actions/document-viewer-base';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadDocument } from 'dashboard/redux/thunk-actions/document-viewer';
import DashboardDocumentViewerView from 'dashboard/views/DashboardDocumentViewerView/DashboardDocumentViewerView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const nullStateMessages: Record<number, string> = {
  401: "You don't have permissions to view this Document.",
  404: 'We couldn’t find that Document.',
};

const nullStateRedirect: Record<number, string> = {
  401: '/dashboard/login',
  404: '/dashboard/inquiries',
};

type Params = { documentId: string };

const DocumentViewerActionTypeDashboardDocumentViewerRoute = (props: PropsFromRedux) => {
  const params = useParams<Params>() as Params; // Will always be defined

  const errorCode = useRef<number>(null);
  const { loading: fileLoading } = useApiLoader({
    loader: () => {
      return props.loadDocument(params.documentId);
    },
    deps: [props.loadDocument, params.documentId],
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load the document.', MessageType.Failure);
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardDocumentViewerView
      document={props.document}
      fileLoading={fileLoading}
      featureFlags={props.featureFlags}
      showMessage={props.showMessage}
      resetBaseState={props.resetBaseState}
      rotateDocument={props.rotateDocument}
      rotation={props.rotation}
      setZoomScale={props.setZoomScale}
      zoomScale={props.zoomScale}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nullStateMessage={nullStateMessages[errorCode.current]}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nullStateRedirect={nullStateRedirect[errorCode.current]}
      incrementZoomScale={props.incrementZoomScale}
      decrementZoomScale={props.decrementZoomScale}
      setZoomVisualValue={props.setZoomVisualValue}
      zoomVisualValue={props.zoomVisualValue}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    featureFlags: state.featureFlags.featureFlags,
    document: state.documentViewer.base.document,
    rotation: state.documentViewer.base.rotation,
    zoomScale: state.documentViewer.base.zoomScale,
    zoomVisualValue: state.documentViewer.base.zoomVisualValue,
  };
};

const mapDispatchToProps = {
  showMessage,
  resetBaseState,
  loadDocument,
  rotateDocument,
  setZoomScale,
  incrementZoomScale,
  decrementZoomScale,
  setZoomVisualValue,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DocumentViewerActionTypeDashboardDocumentViewerRoute);
